import {
  useCallback, useEffect, useRef, useMemo, useState,
} from 'react';
import _ from 'underscore';
import axios, { type AxiosRequestConfig } from 'axios';
import { type IBotConnection } from 'botframework-directlinejs';

import { CookieSessionManager, type ISessionManager, type ICookieSessionManagerOptions } from './SessionManager';
import { type FetchDirectLineChannelToken, type IAdsCopilotProps } from '../types';
import { type ITokenApiPayload, type ITokenApiResponse, ProductGroup } from '../botTypes';

const DEFAULT_REFRESH_TIME = 15 * 1000;
type CopilotMode = 'expanded' | 'collapsed';

function getRequestConfig(authToken?: string | null) {
  return {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  } as AxiosRequestConfig;
}

export const defaultFetchDirectLineChannelToken: FetchDirectLineChannelToken = ({
  endpoint, tokenApi, authToken, userid, username, isMarketingIntegrationEnabled = false,
}) => {
  // strip any trailing slash from endpoint and any leading slash from tokenApi
  const url = `${endpoint.replace(/\/$/, '')}/${tokenApi.replace(/^\//, '')}`;
  if (isMarketingIntegrationEnabled) {
    return axios.post<ITokenApiPayload, ITokenApiResponse>(url, { UserId: userid, ProductGroup: ProductGroup.msads_landing_page }).then(res => ({ token: res.data.token, userid: res.data.userId }));
  }
  return axios.post<ITokenApiPayload, ITokenApiResponse>(url, { UserId: userid, UserName: username }, getRequestConfig(authToken)).then(res => ({ token: res.data.token, userid: res.data.userId }));
};

const REFRESH_TOKEN_URL = 'https://directline.botframework.com/v3/directline/tokens/refresh';
const refreshToken = (oldToken: string | undefined) => axios.post(REFRESH_TOKEN_URL, {}, {
  headers: {
    Authorization: `BEARER ${oldToken}`,
  },
}).then(res => ({ token: res.data.token }));

interface IUseDirectLineTokenRestorationOptions {
  sessionManager: ISessionManager;
  botServiceConfig: IAdsCopilotProps['botServiceConfig'];
  logger?: IAdsCopilotProps['logger'];
  mode: CopilotMode;
  onCopilotModeChange: (param: { mode: CopilotMode }) => void;
  onBeforeValidateToken?: () => void;
  onTokenValidationSuccess?: () => void;
  onTokenValidationFail?: (err: any) => void;
  isMarketingIntegrationEnabled?: boolean;
}

export const useDirectLineTokenRestoration = ({
  sessionManager,
  botServiceConfig: {
    endpoint, tokenApi = '/api/token', authToken, userid, username, fetchOAuthToken, fetchToken,
  },
  logger,
  mode,
  onCopilotModeChange,
  onBeforeValidateToken,
  onTokenValidationSuccess,
  onTokenValidationFail,
  isMarketingIntegrationEnabled,
}: IUseDirectLineTokenRestorationOptions) => {
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<any | null>(null);
  const isRestoringSession = useRef(false);

  const fetchTokenFromBotMT = useCallback(() => {
    const oAuthTokenPromise = fetchOAuthToken ? fetchOAuthToken() : Promise.resolve({ token: authToken });
    return oAuthTokenPromise.then(({ token: oAuthToken }) => {
      const fetchDirectLineChannelTokenFunc = fetchToken || defaultFetchDirectLineChannelToken;
      return fetchDirectLineChannelTokenFunc({
        endpoint, tokenApi, authToken: oAuthToken, userid, username, isMarketingIntegrationEnabled,
      });
    }).then(({ token: newToken }) => {
      setToken(newToken);
    }).catch((e) => {
      setError(e);
    });
  }, [authToken, endpoint, fetchOAuthToken, fetchToken, tokenApi, userid, username, isMarketingIntegrationEnabled]);

  const validateTokenInCookie = useCallback((oldToken: string | undefined) => {
    onBeforeValidateToken?.();

    return refreshToken(oldToken).then(({ token: newToken }) => {
      onTokenValidationSuccess?.();
      setToken(newToken);
    }).catch((err) => {
      onTokenValidationFail?.(err);
      sessionManager.clearSessionInfo();
      throw err;
    });
  }, [
    onBeforeValidateToken,
    onTokenValidationFail,
    onTokenValidationSuccess,
    sessionManager,
  ]);

  useEffect(() => {
    // if user was previously using Copilot chat, open it again
    sessionManager.getSessionInfo().then((sInfo) => {
      if (sInfo != null) {
        const { isOpen, token: oldToken } = sInfo;
        setToken(null);
        logger?.trace(`[Copilot][useDirectLineTokenRestoration] isOpen=${isOpen}, oldToken.length=${oldToken?.length}`);

        if (isOpen && !_.isEmpty(oldToken) && oldToken !== 'tokenPlaceholder') {
          // validate the token in the cookie
          validateTokenInCookie(oldToken).then(() => {
            logger?.trace('[Copilot][useDirectLineTokenRestoration] old token is valid, restoring earlier session...');
            isRestoringSession.current = true;
            // open copilot panel only if the token is valid
            onCopilotModeChange({ mode: 'expanded' });
          }).catch((err) => {
            logger?.trace(`[Copilot][useDirectLineTokenRestoration] oldToken is not valid: ${err}`);
          });
        } else if (isOpen) {
          onCopilotModeChange({ mode: 'expanded' });
        }
      }
    });
  }, [validateTokenInCookie, onCopilotModeChange, sessionManager, logger]);

  const forceRefreshToken = useCallback(() => {
    setToken(null);
    setError(null);
    if (mode === 'expanded') {
      sessionManager.getSessionInfo().then((sInfo) => {
        const oldToken = sInfo && sInfo.token;
        if (oldToken && oldToken.length > 0) {
          validateTokenInCookie(oldToken).catch(() => {
            fetchTokenFromBotMT();
          });
        } else {
          fetchTokenFromBotMT();
        }
      });
    }
  }, [fetchTokenFromBotMT, mode, sessionManager, validateTokenInCookie]);

  useEffect(() => {
    // The flag is true when restoring token from cookie, for this case we do not need to validate and refresh the token
    if (isRestoringSession.current) {
      isRestoringSession.current = false;
      return;
    }
    forceRefreshToken();
  }, [mode, sessionManager, forceRefreshToken]);

  return { token, error, forceRefreshToken };
};

interface IUseConversationTokenStorageOptions {
  sessionManager: ISessionManager;
  refreshTime?: number;
  onConnectionStatusChange?: IAdsCopilotProps['onConnectionStatusChange'];
  onInitDirectLine?: IAdsCopilotProps['onInitDirectLine'];
}

export const useConversationTokenStorage = ({
  sessionManager,
  refreshTime = DEFAULT_REFRESH_TIME,
  onConnectionStatusChange,
  onInitDirectLine,
}: IUseConversationTokenStorageOptions) => {
  const refreshTokenIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dlRef = useRef<IBotConnection | null>(null);

  const saveTokenToCookie = useCallback(() => {
    if (dlRef.current && document.visibilityState === 'visible') {
      const { token } = dlRef.current as any;
      sessionManager.saveSessionInfo({ token });
    }
  }, [sessionManager]);

  const clearIntervalAndEventHandler = useCallback(() => {
    if (refreshTokenIntervalRef.current) {
      clearInterval(refreshTokenIntervalRef.current);
    }
    refreshTokenIntervalRef.current = null;
    document.removeEventListener('visibilitychange', saveTokenToCookie);
  }, [saveTokenToCookie]);

  const finalOnConnectionStatusChange = useCallback<NonNullable<IAdsCopilotProps['onConnectionStatusChange']>>((status) => {
    if (onConnectionStatusChange) {
      onConnectionStatusChange(status);
    }

    if (status === 2) { // Online
      clearIntervalAndEventHandler();
      document.addEventListener('visibilitychange', saveTokenToCookie);
      saveTokenToCookie();
      refreshTokenIntervalRef.current = setInterval(() => {
        saveTokenToCookie();
      }, refreshTime);
    } else if ((status === 3 || status === 4 || status === 5) && refreshTokenIntervalRef.current) { // Ended or expired token
      clearIntervalAndEventHandler();
      refreshTokenIntervalRef.current = null;
    }
  }, [clearIntervalAndEventHandler, onConnectionStatusChange, refreshTime, saveTokenToCookie]);

  const finalOnInitDirectLine = useCallback<NonNullable<IAdsCopilotProps['onInitDirectLine']>>((dl, userId, userName) => {
    if (onInitDirectLine) {
      onInitDirectLine(dl, userId, userName);
    }
    dlRef.current = dl;
  }, [onInitDirectLine]);

  return {
    onConnectionStatusChange: finalOnConnectionStatusChange,
    onInitDirectLine: finalOnInitDirectLine,
  };
};

export const useConversationRestoration = (params: IUseDirectLineTokenRestorationOptions & IUseConversationTokenStorageOptions) => {
  const { token, error, forceRefreshToken } = useDirectLineTokenRestoration(params);
  const {
    onConnectionStatusChange,
    onInitDirectLine,
  } = useConversationTokenStorage(params);

  return {
    token,
    error,
    forceRefreshToken,
    onConnectionStatusChange,
    onInitDirectLine,
  };
};

export const useCookieSessionManager = ({
  domain,
  sessionInfoKey,
  onClearSessionInfo,
  onSaveSessionInfo,
  onGetSessionInfo,
}: ICookieSessionManagerOptions) => {
  const cookieSessionManager = useMemo(() => new CookieSessionManager({
    domain,
    sessionInfoKey,
    onClearSessionInfo,
    onSaveSessionInfo,
    onGetSessionInfo,
  }), [domain, sessionInfoKey, onClearSessionInfo, onSaveSessionInfo, onGetSessionInfo]);

  return cookieSessionManager;
};

export const useConversationRestorationWithCookie = (params: Omit<IUseDirectLineTokenRestorationOptions & IUseConversationTokenStorageOptions, 'sessionManager'> & ICookieSessionManagerOptions) => {
  const sessionManager = useCookieSessionManager(params);
  const result = useConversationRestoration({
    ...params,
    sessionManager,
  });

  return result;
};
